import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../exam.js';

/** A `BaseExtractionProgress`/`SolutionProgress`-shaped step has at least started. */
function isRunStartedOrDone(status: unknown): boolean {
  return status === 'IN_PROGRESS' || status === 'COMPLETED';
}

/** A file was uploaded (non-empty id list) or a run has at least started — either is "work has begun". */
function hasArtifactWork(idCount: number, status: unknown): boolean {
  return idCount > 0 || isRunStartedOrDone(status);
}

/**
 * Whether the exam-creator (the exam-creation tool/job AND the exam-editor canvas built on top of it) is
 * locked for this exam — the ONE definition every enforcement point shares: `exam-creation.job.ts`'s own
 * `ready()`, `create-exam.tool.ts`'s early refusal and its `createExamAvailability` (so the tool is not
 * even offered), `exam-editor-api.ts`'s two mutations, and the `ExamEditor` client's own read-only banner.
 * A single shared predicate is the point — if the agent were told a different answer than what the tool
 * actually enforces, it could offer a change it then fails to make.
 *
 * LOCKED once master-solution or grading-scheme work has begun for this exam — a file was uploaded, or a
 * generation/extraction run has started or finished — or once student submissions have arrived, or once
 * any answer has been extracted from one. From that point on the task tree is what that work and those
 * answers hang off (both bind by taskNumber), so rewriting it (a fresh exam-creation run, a refinement
 * patch, or a manual canvas edit) would silently detach the answers or drop solution content nothing here
 * reconciles back in. Same reasoning `exam-creation.job.ts`'s pre-existing submissions check already used,
 * extended to solution content.
 *
 * BOTH `uploadProgress.submissionsFound` AND `answerExtractionProgress` ARE CHECKED, deliberately not just
 * one: `submissionsFound` is a counter only the split flow (`submission-split.ts`) increments — a
 * submission created directly (`createSubmissionMutation`, e.g. a teacher manually adding one in the
 * submission-check canvas) never touches it, so it alone can under-report. Extraction having started or
 * finished, or having left at least one unit done, cannot be missed that way — it is written by the step
 * that actually reads the submission's answers, regardless of how the submission arrived.
 *
 * Loosely typed on purpose: callers hand this anything from a full `Exam` entity to a narrow Mongo
 * projection, and the fields read here are plain data either way.
 */
export function isExamCreatorLocked(exam: any): boolean {
  const state = exam?.examState;
  const hasMasterSolutionWork = hasArtifactWork(
    exam?.masterSolutionIds?.length ?? 0,
    state?.masterSolutionProgress?.status,
  );
  const hasGradingSchemeWork = hasArtifactWork(exam?.gradingSchemeIds?.length ?? 0, state?.gradingSchemeProgress?.status);
  const hasSubmissions = (state?.uploadProgress?.submissionsFound ?? 0) > 0;
  const hasExtractedAnswers =
    isRunStartedOrDone(state?.answerExtractionProgress?.status) ||
    (state?.answerExtractionProgress?.finishedUnits?.length ?? 0) > 0;

  return hasMasterSolutionWork || hasGradingSchemeWork || hasSubmissions || hasExtractedAnswers;
}

/**
 * Whether ANY answer has arrived for this exam yet — scanned and split, extracted, or handed in through the
 * online student-exam flow. Same three checks `isExamCreatorLocked` already makes for the first two
 * (`submissionsFound`, `answerExtractionProgress`), plus `onlineSubmissionReceived` for the third — pulled
 * out on its own because it answers a narrower question than the lock does: "has the student who owns this
 * exam actually submitted something", used to gate master-solution/grading-scheme generation on a student's
 * own self-practice exam until they submit (see master-solution.tool.ts / grading-scheme.tool.ts).
 */
export function hasReceivedAnySubmission(exam: any): boolean {
  const state = exam?.examState;
  const hasSubmissions = (state?.uploadProgress?.submissionsFound ?? 0) > 0;
  const hasExtractedAnswers =
    isRunStartedOrDone(state?.answerExtractionProgress?.status) ||
    (state?.answerExtractionProgress?.finishedUnits?.length ?? 0) > 0;
  const hasOnlineSubmission = Boolean(state?.onlineSubmissionReceived);
  return hasSubmissions || hasExtractedAnswers || hasOnlineSubmission;
}

const LOCK_PROJECTION = {
  masterSolutionIds: 1,
  gradingSchemeIds: 1,
  'examState.masterSolutionProgress.status': 1,
  'examState.gradingSchemeProgress.status': 1,
  'examState.uploadProgress.submissionsFound': 1,
  'examState.answerExtractionProgress.status': 1,
  'examState.answerExtractionProgress.finishedUnits': 1,
} as const;

/** Same question for a caller holding only an id — projected, `false` on error, job-safe (no session). */
export async function resolveIsExamCreatorLocked(examId: string): Promise<boolean> {
  try {
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId }, { projection: LOCK_PROJECTION as any });
    return isExamCreatorLocked(exam);
  } catch (err) {
    logger.error({ err, examId }, 'Failed to resolve whether the exam creator is locked');
    return false;
  }
}
